/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useHistory } from 'react-router-dom';
import {
  Dispatch,
  Fragment,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import Table, { TableProps } from 'antd/lib/table';

// local imports
import { useStyles } from '~src/hooks/useStyles';
import IconButton from '~components/_common/IconButton/IconButton';
import { __DEV__ } from '~src/config';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { formatDate } from '~src/utils/formatDate';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import TenderRegistration from '~src/assets/nav-icons/file-tender-hammer.svg';
import { DbFocusOptions, DbShareTender } from '~src/types/db';
import { TableRowSelection } from 'antd/lib/table/interface';
import { sortByNumberProp } from '~src/utils/sort';
import { styleFn } from '../FileTables/FileTable.style';
import { useSharesSimultaneously } from '~src/hooks/api-hooks/useSharesSimultaneously';
import CachedSharesContext from '~src/contexts/CachedSharesContext';
import { useQuery } from 'react-query';
import {
  ShareKeyValidationResponseDto,
  validateShareKeys,
} from '~src/services/api/shares';

export type CachedTenderShareDetails = {
  shareKey?: string | null;
  shareId?: string;
};

export type CachedShareTableEntry = CachedTenderShareDetails & DbShareTender;

// Row type extended with optional key-validity info
export type CachedShareTableRow = CachedShareTableEntry & {
  is_valid?: boolean;
  validity_status?: {
    is_key_active?: boolean;
    is_key_expired?: boolean;
    is_key_claimed?: boolean;
    is_key_rejected?: boolean;
  };
};

export interface CachedSharedTableProps
  extends TableProps<CachedShareTableEntry> {
  selectedKeys?: TableRowSelection<CachedShareTableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<CachedShareTableEntry>['onSelect'];
  // @ts-ignore
  onSelectAll?: TableRowSelection<CachedShareTableEntry['onSelectAll']>;
  highlightedRow?: CachedShareTableEntry;
  suMode?: boolean;
  setSelectedItems: Dispatch<SetStateAction<CachedShareTableEntry[]>>;
  onEmptyOverlay?: () => void;
  focusOptions?: DbFocusOptions[];
}

const sortByUpdatedAt = sortByNumberProp('updatedAt');

/** Recent files table */
export const CachedSharesTable: React.FC<CachedSharedTableProps> = ({
  selectedKeys,
  highlightedRow,
  onSelect,
  setSelectedItems,
  ...tableProps
}) => {
  const styles = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();

  const { fileVersionsContext } = useContext(FileVersionsContext);

  const {
    cachedSharesContext,
    setCurrentCachedShare,
    setCachedSharesContext,
  } = useContext(CachedSharesContext);

  const { sharesList, expiredSharesList } = useSharesSimultaneously(
    cachedSharesContext
  );

  // Remove only the exact expired shareId + shareKey pair
  // from both localStorage and in-memory context.
  useEffect(() => {
    if (expiredSharesList.length > 0) {
      const updatedCachedShares = cachedSharesContext.filter(
        ({ shareId, shareKey }) =>
          !expiredSharesList.some(
            (share) => share.shareId === shareId && share.shareKey === shareKey
          )
      );

      setCachedSharesContext(updatedCachedShares);
      localStorage.setItem('shares', JSON.stringify(updatedCachedShares));
    }
  }, [expiredSharesList, cachedSharesContext, setCachedSharesContext]);

  // Prepare payload for batch key-validation
  const shareKeysForValidation = useMemo(
    () =>
      cachedSharesContext
        .filter((share) => share.shareId && share.shareKey)
        .map((share) => ({
          share_id: share.shareId as string,
          key: share.shareKey as string,
        })),
    [cachedSharesContext]
  );

  // Batch validate all cached share-keys
  const { data: shareKeysValidity } = useQuery<ShareKeyValidationResponseDto[]>(
    ['cachedSharesKeyValidity', { shares: shareKeysForValidation }],
    () => validateShareKeys(shareKeysForValidation),
    {
      enabled: shareKeysForValidation.length > 0,
      refetchOnWindowFocus: false,
      retry: false,
    }
  );

  // Map "shareId:key" -> validity info for quick lookup in table
  const keyValidityMap = useMemo(() => {
    const map: Record<string, ShareKeyValidationResponseDto> = {};

    if (!shareKeysValidity || !Array.isArray(shareKeysValidity)) {
      return map;
    }

    shareKeysValidity.forEach((item) => {
      if (!item?.share_id || !item?.key) return;
      map[`${item.share_id}:${item.key}`] = item;
    });

    return map;
  }, [shareKeysValidity]);

  const columns = useMemo<TableProps<CachedShareTableRow>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 50,
        align: 'center',
        render: (type, record) => {
          return {
            children: (
              <div style={{ marginLeft: '10px', width: 30, height: 30 }}>
                <NavIcon icon={TenderRegistration} />
              </div>
            ),
          };
        },
      },
      {
        title: intl.formatMessage({
          id: 'share-key-status',
          defaultMessage: 'Key status',
        }),
        dataIndex: 'shareKey',
        width: 100,
        render: (_, record) => {
          const key =
            record.shareId && record.shareKey
              ? `${record.shareId}:${record.shareKey}`
              : null;

          const validity = key ? keyValidityMap[key] : undefined;

          if (!validity || !validity.validity_status) {
            return intl.formatMessage({
              id: 'share-key-status-unknown',
              defaultMessage: 'Unknown',
            });
          }

          const {
            is_key_claimed,
            is_key_expired,
            is_key_rejected,
          } = validity.validity_status;

          let labelId = 'share-key-status-valid';
          let defaultLabel = 'Valid';
          let color = '#52c41a';

          if (!validity.is_valid || is_key_expired) {
            labelId = 'share-key-status-expired';
            defaultLabel = 'Expired';
            color = '#fa541c';
          } else if (is_key_claimed) {
            labelId = 'share-key-status-claimed';
            defaultLabel = 'Claimed';
            color = '#fa8c16';
          } else if (is_key_rejected) {
            labelId = 'share-key-status-rejected';
            defaultLabel = 'Rejected';
            color = '#d46b08';
          }

          return (
            <span style={{ color, fontWeight: 500 }}>
              {intl.formatMessage({
                id: labelId,
                defaultMessage: defaultLabel,
              })}
            </span>
          );
        },
      },
      {
        title: intl.formatMessage({ id: 'title' }),
        dataIndex: 'title',
        sorter: (a, b) => a.title.localeCompare(b.title),
        render: (title, record) => (
          <Fragment>
            {title}
            {/* {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )} */}
          </Fragment>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) =>
          (a.description || '').localeCompare(b.description || ''),

        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'active-until' }),
        key: 'calculated_deadline',
        sorter: (a, b) => {
          const getEffective = (record) => {
            const p = Number(record.active_until) || 0;
            const d = Number(record.deadline_at) || 0;
            const pValid = p > 3601;
            const dValid = d > 3601;

            if (pValid && dValid) return Math.min(p, d);
            if (dValid) return d;
            if (pValid) return p;
            return 0;
          };
          return getEffective(a) - getEffective(b);
        },
        width: 200,
        ellipsis: true,
        render: (_, record) => {
          // Dates
          const participationDeadline = Number(record.active_until) || 0;
          const mainDeadline = Number(record.deadline_at) || 0;

          // date variable is considered zero if the unix timestamp is 3601 or less
          const isPValid = participationDeadline > 3601;
          const isDValid = mainDeadline > 3601;

          let finalTimestamp = 0;

          // show the participation deadline if it is set, and it is smaller than deadline, otherwise it should show the deadline variable instead.
          if (isPValid && isDValid) {
            finalTimestamp =
              participationDeadline < mainDeadline
                ? participationDeadline
                : mainDeadline;
          } else if (isDValid) {
            finalTimestamp = mainDeadline;
          } else if (isPValid) {
            finalTimestamp = participationDeadline;
          }

          if (finalTimestamp <= 3601) {
            return '';
          }

          return <div>{formatDate(finalTimestamp, 'short')}</div>;
        },
      },
    ],
    [fileVersionsContext, intl, styles.icons, styles.small, keyValidityMap]
  );

  return (
    <Table<CachedShareTableRow>
      columns={columns}
      dataSource={sharesList}
      onRow={(record, index) => {
        return {
          onClick: () => {
            if (record.shareId && record.shareKey != null) {
              // Set correct current cached share BEFORE navigating,
              // so details view immediately gets proper key.
              setCurrentCachedShare({
                shareId: record.shareId,
                shareKey: record.shareKey,
              });
            }
            // Go directly to /invitations/{shareId}-{shareKey}/details
            history.push(
              `/invitations/${record.shareId}-${record.shareKey}/details`
            );
          },
        };
      }}
      pagination={false}
      bordered
      showSorterTooltip={false}
      css={[
        styles.table,
        { marginTop: '20px' },
        tableProps.scroll?.y && {
          '.ant-table-body': {
            height: tableProps.scroll.y,
          },
        },
      ]}
      className="main-content-window"
      {...tableProps}
    />
  );
};
